/**
 * Multi-tier game detection system with weighted keyword scoring
 * and confidence levels for accurate TCG and Sports Card categorization.
 */

import { 
  POKEMON_NAMES, 
  POKEMON_TRAINER_NAMES, 
  POKEMON_SET_NAMES,
  POKEMON_PRODUCT_KEYWORDS 
} from '@/constants/pokemonNameDatabase';

// Detection result with confidence scoring
export interface DetectionResult {
  game: string;
  confidence: number; // 0.0 - 1.0
  needsReview: boolean;
  matchedKeywords: string[];
}

// Keyword weights
const WEIGHT = {
  DEFINITIVE: 10,  // Instant match, confidence = 1.0
  STRONG: 6,       // Exclusive to this game
  MODERATE: 3,     // Mostly exclusive
  WEAK: 1,         // May overlap with other games
  NEGATIVE: -5,    // Suggests NOT this game
} as const;

// Confidence thresholds
const THRESHOLD = {
  HIGH_CONFIDENCE: 0.55,   // No review needed
  MEDIUM_CONFIDENCE: 0.4,  // Flag for review but use detected game
  MIN_SCORE: 3,            // Minimum score to return a game
  DOMINANT_MARGIN: 5,      // If winner beats runner-up by this much, don't flag review
} as const;


interface GameKeywordConfig {
  definitive: string[];   // Instant match (weight: 10)
  strong: string[];       // Exclusive indicators (weight: 5)
  moderate: string[];     // Mostly exclusive (weight: 3)
  weak: string[];         // May overlap (weight: 1)
  negative: string[];     // Suggests NOT this game (weight: -5)
}

// Build Pokemon moderate keywords from database
const POKEMON_MODERATE_KEYWORDS = [
  ...POKEMON_NAMES.slice(0, 200), // Top 200 Pokemon for performance
  ...POKEMON_TRAINER_NAMES,
];

const GAME_DETECTION_CONFIG: Record<string, GameKeywordConfig> = {
  pokemon: {
    definitive: [
      'pokemon', 'pokémon', 'ptcg', 'ポケモン', 'the pokemon company',
      'pokemon tcg', 'pokémon tcg', 'pocket monster',
    ],
    strong: [
      // Era prefixes (exclusive to Pokemon)
      'sv-', 'swsh-', 'sm-', 'xy-', 'bw-', 'hgss-', 'dp-', 'ex-',
      // Exclusive product types
      'elite trainer box', ' etb', 'booster bundle', 'ultra premium collection',
      'trainer gallery', 'vmax', 'vstar', 'v-union', 'radiant collection',
      // All Pokemon set names
      ...POKEMON_SET_NAMES,
      // Product keywords
      ...POKEMON_PRODUCT_KEYWORDS,
    ],
    moderate: POKEMON_MODERATE_KEYWORDS,
    weak: [
      // Generic terms that could be other games but often are Pokemon
      'booster box', 'booster pack', 'collection box', 'gift box', 'tin',
      'blister', 'starter deck', 'theme deck',
    ],
    negative: [
      'yu-gi-oh', 'yugioh', 'magic the gathering', 'magic: the gathering', 'mtg',
      'lorcana', 'one piece', 'onepiece', 'digimon', 'dragon ball', 'dragonball',
      'flesh and blood', 'weiss schwarz', 'vanguard', 'star wars', 'final fantasy',
      'union arena', 'universus', 'gundam', 'grand archive', 'battle spirits',
      'shadowverse', 'bandai op-', 'bandai digimon',
    ],
  },
  yugioh: {
    definitive: [
      'yu-gi-oh', 'yugioh', 'yu-gi-oh!', 'ygo', 'konami tcg',
    ],
    strong: [
      // Set prefixes
      'lob-', 'mrd-', 'psv-', 'lod-', 'ioc-', 'led-', 'mp-', 'blvo-', 'rotd-',
      'etco-', 'dama-', 'bode-', 'difo-', 'pote-', 'dabl-', 'phhy-', 'cyac-', 'agov-',
      // Product types
      'structure deck', 'legendary duelists', 'battles of legend', 'tin of the ',
      'gold rare', 'ghost rare', 'prismatic secret', 'duel terminal', 'speed duel',
      'duelist pack', 'maximum gold', 'mega pack', 'duelist nexus', 'age of overlord',
      // Iconic cards
      'blue-eyes white dragon', 'dark magician', 'exodia', 'red-eyes black dragon',
    ],
    moderate: [
      'blue-eyes', 'dark magician', 'exodia', 'red-eyes', 'elemental hero',
      'crystal beast', 'cyber dragon', 'stardust dragon', 'dragon maid',
      'sky striker', 'ash blossom', 'maxx c', 'called by the grave',
      'number ', 'xyz dragon', 'synchro', 'link monster', 'pendulum',
      'pharaoh', 'duelist', 'millennium',
    ],
    weak: [
      'booster box', 'booster pack', 'duel monsters',
    ],
    negative: [
      'pokemon', 'pokémon', 'lorcana', 'mtg', 'magic the gathering',
      'one piece', 'digimon', 'dragon ball', 'pikachu', 'charizard',
    ],
  },
  mtg: {
    definitive: [
      'magic the gathering', 'magic: the gathering', 'mtg ', ' mtg',
      'wizards of the coast', 'hasbro mtg', 'wotc',
    ],
    strong: [
      // Recent sets
      'modern horizons', 'murders at karlov', 'bloomburrow', 'duskmourn',
      'foundations', 'outlaws of thunder', 'lost caverns', 'wilds of eldraine',
      'lord of the rings', 'march of the machine', 'phyrexia', 'brothers war',
      'dominaria united', 'streets of new capenna', 'kamigawa neon',
      'innistrad midnight', 'innistrad crimson', 'strixhaven', 'kaldheim',
      'zendikar rising', 'ikoria', 'theros beyond',
      // Historical
      'throne of eldraine', 'core set 2020', 'core set 2021', 'core set 2019',
      'adventures in the forgotten realms', 'midnight hunt', 'crimson vow',
      'kamigawa neon dynasty', 'new capenna', 'dominaria remastered',
      'commander legends', 'commander masters', 'double masters',
      'time spiral remastered', 'modern masters', 'eternal masters', 'battlebond',
      'unfinity', 'unstable', 'unglued',
      // Universes Beyond
      'warhammer 40k mtg', 'doctor who mtg', 'fallout mtg', "assassin's creed mtg",
      'mtg final fantasy', 'mtg marvel', 'mtg spider-man',
      // 2025-2026
      'aetherdrift', 'tarkir dragonstorm', 'edge of eternities',
      // Classic sets
      'ravnica', 'zendikar', 'dominaria', 'amonkhet', 'kaladesh', 'tarkir',
      'theros', 'innistrad', 'mirrodin', 'lorwyn', 'shadowmoor',
      'war of the spark', 'guilds of ravnica', 'baldur', 'battle for baldur',
      // Products
      'commander deck', 'planeswalker deck', 'secret lair', 'collector booster',
      'draft booster', 'set booster', 'prerelease kit', 'jumpstart',
    ],

    moderate: [
      'planeswalker', 'mana', 'mythic rare', 'borderless', 'foil etched',
      'serialized', 'extended art', 'commander', 'edh',
    ],
    weak: [
      'booster box', 'booster pack', 'bundle',
    ],
    negative: [
      'pokemon', 'pokémon', 'yu-gi-oh', 'yugioh', 'lorcana', 'one piece',
      'digimon', 'dragon ball', 'pikachu', 'charizard',
    ],
  },
  lorcana: {
    definitive: [
      'lorcana', 'disney lorcana', 'ravensburger tcg',
    ],
    strong: [
      'the first chapter', 'rise of the floodborn', 'into the inklands',
      'ursula return', "ursula's return", 'shimmering skies', 'azurite sea',
      "archazia's island", 'illumineer', 'glimmer', 'inklands',
    ],
    moderate: [
      'enchanted card', 'cold foil lorcana', 'ink color',
    ],
    weak: [
      'mickey', 'elsa', 'stitch', 'maleficent', 'ursula', 'maui', 'moana',
      'belle', 'beast', 'aladdin', 'jasmine', 'simba', 'scar',
    ],
    negative: [
      'pokemon', 'pokémon', 'yu-gi-oh', 'yugioh', 'mtg', 'magic the gathering',
      'pikachu', 'charizard',
    ],
  },
  onepiece: {
    definitive: [
      'one piece', 'onepiece', 'one piece tcg', 'op tcg',
    ],
    strong: [
      // Bandai set codes
      'op01', 'op02', 'op03', 'op04', 'op05', 'op06', 'op07', 'op08', 'op09', 'op10',
      'op11', 'op12', 'op13', 'op14',
      'op-01', 'op-02', 'op-03', 'op-04', 'op-05', 'op-06', 'op-07', 'op-08', 'op-09', 'op-10',
      'op-11', 'op-12', 'op-13', 'op-14',
      'st01', 'st02', 'st03', 'st04', 'st05', 'st06', 'st07', 'st08', 'st09', 'st10',
      'st11', 'st12', 'st13', 'st14', 'st15', 'st16', 'st17', 'st18', 'st19', 'st20',
      'eb01', 'eb02', 'prb01',
      // Set names
      'romance dawn', 'paramount war', 'pillars of strength', 'kingdoms of intrigue',
      'awakening of the new era', 'wings of the captain', '500 years in the future',
      'two legends', 'memorial collection', 'emperors in the new world',
      'a fist of divine speed', 'red memorial', 'royal blood', 'flag bearers',
      'extra booster', 'premium booster op',
    ],

    moderate: [
      'luffy', 'zoro', 'nami', 'sanji', 'chopper', 'robin', 'franky', 'brook',
      'jinbe', 'shanks', 'kaido', 'big mom', 'whitebeard', 'ace', 'sabo', 'law',
      'kid', 'yamato', 'boa', 'crocodile', 'doflamingo', 'katakuri',
      'straw hat', 'going merry', 'thousand sunny',
    ],
    weak: [
      'bandai', 'booster box', 'booster pack',
    ],
    negative: [
      'pokemon', 'pokémon', 'yu-gi-oh', 'yugioh', 'mtg', 'magic the gathering',
      'lorcana', 'pikachu', 'charizard', 'digimon',
    ],
  },
  digimon: {
    definitive: [
      'digimon', 'digimon tcg', 'digimon card game',
    ],
    strong: [
      // Set prefixes
      'bt01', 'bt02', 'bt03', 'bt04', 'bt05', 'bt06', 'bt07', 'bt08', 'bt09', 'bt10',
      'bt11', 'bt12', 'bt13', 'bt14', 'bt15', 'bt16', 'bt17', 'bt18',
      'ex01', 'ex02', 'ex03', 'ex04', 'ex05', 'ex06', 'ex07', 'ex08',
      'digi-egg', 'tamer card',
    ],
    moderate: [
      'agumon', 'gabumon', 'patamon', 'gatomon', 'veemon', 'guilmon', 'renamon',
      'omnimon', 'wargreymon', 'metalgarurumon', 'imperialdramon', 'alphamon',
      'magnamon', 'jesmon', 'gallantmon', 'beelzemon',
    ],
    weak: [
      'bandai', 'booster box', 'booster pack',
    ],
    negative: [
      'pokemon', 'pokémon', 'yu-gi-oh', 'yugioh', 'mtg', 'magic the gathering',
      'lorcana', 'one piece', 'pikachu', 'charizard',
    ],
  },
  dragonball: {
    definitive: [
      'dragon ball', 'dragonball', 'dbs tcg', 'dbscg', 'dragon ball super',
      'dbfw', 'dragon ball fusion world',
    ],
    strong: [
      // Fusion World sets
      'fb01', 'fb02', 'fb03', 'fb04', 'fs01', 'fs02', 'fs03', 'fs04', 'fs05',
      'awakened pulse', 'blazing aura', 'zenkai series', 'fusion world',
    ],
    moderate: [
      'goku', 'vegeta', 'gohan', 'piccolo', 'frieza', 'cell', 'buu', 'broly',
      'beerus', 'whis', 'jiren', 'trunks', 'krillin', 'android 17', 'android 18',
      'saiyan', 'super saiyan', 'ultra instinct', 'kamehameha',
    ],
    weak: [
      'bandai', 'booster box', 'booster pack',
    ],
    negative: [
      'pokemon', 'pokémon', 'yu-gi-oh', 'yugioh', 'mtg', 'magic the gathering',
      'lorcana', 'one piece', 'digimon', 'pikachu', 'charizard',
    ],
  },
  fab: {
    definitive: [
      'flesh and blood', 'flesh & blood', 'fab tcg', 'fabtcg',
    ],
    strong: [
      'welcome to rathe', 'arcane rising', 'crucible of war', 'monarch',
      'tales of aria', 'everfest', 'uprising', 'dynasty', 'outsiders',
      'dusk till dawn', 'bright lights', 'heavy hitters', 'rosetta',
      'part the mistveil', 'hunted',
    ],
    moderate: [
      'bravo', 'katsu', 'rhinar', 'dorinthea', 'prism', 'levia', 'chane',
      'briar', 'oldhim', 'iyslander', 'dromai', 'fai', 'arakni', 'azalea',
      'majestic', 'legendary fab', 'cold foil fab',
    ],
    weak: [
      'booster box', 'booster pack',
    ],
    negative: [
      'pokemon', 'pokémon', 'yu-gi-oh', 'yugioh', 'mtg', 'magic the gathering',
      'lorcana', 'one piece', 'digimon', 'dragon ball', 'pikachu', 'charizard',
    ],
  },
  starwars: {
    definitive: [
      'star wars unlimited', 'star wars: unlimited', 'sw unlimited', 'swu ',
    ],
    strong: [
      'spark of rebellion', 'shadows of the galaxy', 'twilight of the republic',
    ],
    moderate: [
      'luke skywalker', 'darth vader', 'leia organa', 'han solo', 'chewbacca',
      'yoda', 'palpatine', 'boba fett', 'mandalorian', 'grogu', 'ahsoka',
      'anakin', 'obi-wan', 'kenobi', 'rey', 'kylo ren',
      'jedi', 'sith', 'rebel', 'empire', 'galactic',
    ],
    weak: [
      'star wars', 'starwars', 'booster box', 'booster pack',
    ],
    negative: [
      'pokemon', 'pokémon', 'yu-gi-oh', 'yugioh', 'mtg', 'magic the gathering',
      'lorcana', 'one piece', 'digimon', 'pikachu', 'charizard',
    ],
  },
  weiss: {
    definitive: [
      'weiss schwarz', 'weiss schwarz tcg', 'ws tcg',
    ],
    strong: [
      'hololive', 'bang dream', 'love live', 'sword art online ws',
      'fate ws', 'attack on titan ws', 're:zero', 'konosuba', 'date a live',
      'spy x family ws', 'chainsaw man ws', 'kaguya-sama', 'quintessential quintuplets',
      'mushoku tensei', 'overlord ws',
    ],
    moderate: [
      'bushiroad', 'signed card ws', 'sp card', 'ssp card', 'sec card',
      'trial deck ws',
    ],
    weak: [
      'booster box', 'booster pack', 'anime',
    ],
    negative: [
      'pokemon', 'pokémon', 'yu-gi-oh', 'yugioh', 'mtg', 'magic the gathering',
      'lorcana', 'one piece', 'digimon', 'pikachu', 'charizard', 'vanguard',
    ],
  },
  vanguard: {
    definitive: [
      'cardfight vanguard', 'cardfight!! vanguard', 'cfv ', ' cfv',
    ],
    strong: [
      'overdress', 'divinez', 'dear days', 'd series', 'v series', 'g series',
      'premium collection vanguard', 'start deck vanguard', 'trial deck vanguard',
    ],
    moderate: [
      'dragon empire', 'dark states', 'brandt gate', 'keter sanctuary',
      'stoicheia', 'lyrical monasterio',
    ],
    weak: [
      'bushiroad', 'vanguard', 'booster box', 'booster pack',
    ],
    negative: [
      'pokemon', 'pokémon', 'yu-gi-oh', 'yugioh', 'mtg', 'magic the gathering',
      'lorcana', 'one piece', 'digimon', 'weiss', 'pikachu', 'charizard',
    ],
  },
  finalfantasy: {
    definitive: [
      'final fantasy tcg', 'fftcg', 'ff tcg', 'square enix tcg',
    ],
    strong: [
      'opus i', 'opus ii', 'opus iii', 'opus iv', 'opus v', 'opus vi',
      'opus vii', 'opus viii', 'opus ix', 'opus x', 'opus xi', 'opus xii',
      'opus xiii', 'opus xiv', 'opus xv', 'opus xvi', 'opus xvii', 'opus xviii',
    ],
    moderate: [
      'cloud strife', 'tifa lockhart', 'aerith', 'sephiroth', 'squall',
      'rinoa', 'zidane', 'tidus', 'yuna', 'lightning', 'noctis', 'clive',
      'ffvii', 'ffviii', 'ffix', 'ffx', 'ffxii', 'ffxiii', 'ffxiv', 'ffxv', 'ffxvi',
    ],
    weak: [
      'final fantasy', 'booster box', 'booster pack', 'starter set',
    ],
    negative: [
      'pokemon', 'pokémon', 'yu-gi-oh', 'yugioh', 'mtg', 'magic the gathering',
      'lorcana', 'one piece', 'digimon', 'pikachu', 'charizard',
    ],
  },
  unionarena: {
    definitive: [
      'union arena', 'unionarena', 'ua tcg',
    ],
    strong: [
      'jujutsu kaisen ua', 'hunter x hunter ua', 'demon slayer ua',
      'bleach ua', 'black clover ua', 'my hero academia ua', 'code geass ua',
      'tales of arise ua', 'gintama ua',
    ],
    moderate: [
      'gojo satoru', 'itadori yuji', 'sukuna', 'megumi', 'nobara',
      'gon freecss', 'killua', 'kurapika', 'tanjiro', 'nezuko',
      'ichigo kurosaki', 'byakuya', 'asta', 'yami',
    ],
    weak: [
      'bandai', 'booster box', 'booster pack',
    ],
    negative: [
      'pokemon', 'pokémon', 'yu-gi-oh', 'yugioh', 'mtg', 'magic the gathering',
      'lorcana', 'one piece', 'digimon', 'pikachu', 'charizard', 'universus',
    ],
  },
  universus: {
    definitive: [
      'universus ccg', 'universus tcg', 'jasco universus',
    ],
    strong: [
      'my hero academia universus', 'attack on titan universus',
      'cowboy bebop universus', 'critical role universus', 'rwby universus',
      'mortal kombat universus', 'street fighter universus',
    ],
    moderate: [
      'deku universus', 'bakugo universus', 'all might universus',
      'todoroki universus', 'eren universus', 'mikasa universus', 'levi universus',
    ],
    weak: [
      'universus', 'jasco', 'booster box', 'booster pack',
    ],
    negative: [
      'pokemon', 'pokémon', 'yu-gi-oh', 'yugioh', 'mtg', 'magic the gathering',
      'lorcana', 'one piece', 'digimon', 'pikachu', 'charizard', 'union arena',
    ],
  },
  gundam: {
    definitive: [
      'gundam card game', 'gundamcg', 'gundam tcg',
    ],
    strong: [
      'mobile suit gundam', 'gundam wing', 'gundam seed', 'gundam 00',
      'iron-blooded orphans', 'gundam unicorn', 'hathaway', 'witch from mercury',
    ],
    moderate: [
      'suletta', 'aerial', 'rx-78', 'zaku', 'char aznable', 'amuro ray',
    ],
    weak: [
      'gundam', 'bandai', 'booster box', 'booster pack',
    ],
    negative: [
      'pokemon', 'pokémon', 'yu-gi-oh', 'yugioh', 'mtg', 'magic the gathering',
      'lorcana', 'one piece', 'digimon', 'pikachu', 'charizard',
    ],
  },
  grandarchive: {
    definitive: [
      'grand archive', 'grandarchive', 'gatcg', 'grand archive tcg',
    ],
    strong: [
      'dawn of ashes', 'fractured crown', 'alchemical revolution',
      'mercurial heart', 'mortal ambition', 'weebs of the shore', 'alter edition',
    ],
    moderate: [
      'champion grand archive', 'material card', 'regalia card', 'ally card',
    ],
    weak: [
      'booster box', 'booster pack',
    ],
    negative: [
      'pokemon', 'pokémon', 'yu-gi-oh', 'yugioh', 'mtg', 'magic the gathering',
      'lorcana', 'one piece', 'digimon', 'pikachu', 'charizard',
    ],
  },
  battlespirits: {
    definitive: [
      'battle spirits saga', 'battle spirits tcg', 'bss tcg',
    ],
    strong: [
      'saga brave', 'collaboration booster bs', 'mega deck bs', 'dawn of history bs',
    ],
    moderate: [],
    weak: [
      'battle spirits', 'bandai', 'booster box', 'booster pack',
    ],
    negative: [
      'pokemon', 'pokémon', 'yu-gi-oh', 'yugioh', 'mtg', 'magic the gathering',
      'lorcana', 'one piece', 'digimon', 'pikachu', 'charizard',
    ],
  },
  shadowverse: {
    definitive: [
      'shadowverse evolve', 'shadowverse tcg', 'sv evolve',
    ],
    strong: [
      'cygames tcg', 'flame of salvation', 'realm of the gods', 'chronogenesis sv',
    ],
    moderate: [
      'leader card sv', 'evolve deck',
    ],
    weak: [
      'shadowverse', 'booster box', 'booster pack',
    ],
    negative: [
      'pokemon', 'pokémon', 'yu-gi-oh', 'yugioh', 'mtg', 'magic the gathering',
      'lorcana', 'one piece', 'digimon', 'pikachu', 'charizard',
    ],
  },
  riftbound: {
    definitive: [
      'riftbound', 'league of legends tcg', 'lol tcg', 'riftbound tcg',
    ],
    strong: [
      'origins riftbound', 'proving grounds', 'ashen forge',
      'ogn-', 'ogs-', 'ogn01', 'ogs01',
    ],
    moderate: [
      'jinx', 'yasuo', 'ahri', 'lux', 'garen', 'darius', 'thresh',
      'zed', 'ezreal', 'lee sin', 'katarina', 'akali', 'viktor',
      'runeterra', 'demacia', 'noxus', 'ionia', 'piltover', 'zaun',
      'shurima', 'freljord', 'bandle city', 'shadow isles', 'targon',
      'league of legends',
    ],
    weak: [
      'booster pack', 'booster box', 'starter deck', 'riot',
    ],
    negative: [
      'pokemon', 'pokémon', 'yu-gi-oh', 'yugioh', 'mtg', 'magic the gathering',
      'lorcana', 'one piece', 'digimon', 'pikachu', 'charizard',
    ],
  },
  

  
  // ============= Sports Cards =============
  baseball: {
    definitive: [
      'topps baseball', 'panini baseball', 'mlb trading cards', 'bowman baseball',
    ],
    strong: [
      // Major brands/products
      'bowman chrome', 'bowman draft', 'bowman 1st', '1st bowman',
      'topps chrome', 'topps heritage', 'topps finest', 'topps update',
      'topps series 1', 'topps series 2', 'topps opening day',
      'stadium club', 'gypsy queen', 'archives', 'allen ginter', 'allen & ginter',
      'diamond kings', 'donruss baseball', 'panini prizm baseball', 'panini select baseball',
      'topps dynasty', 'topps tribute', 'topps inception', 'topps tier one',
      'topps museum', 'topps luminaries', 'topps definitive',
    ],
    moderate: [
      // Generic sports card terms that are common in baseball
      'rookie card', ' rc ', ' /99', ' /50', ' /25', ' /10', ' /5', ' /1',
      'auto ', 'autograph', 'patch card', 'relic card', 'game used',
      'refractor', 'prizm', 'sp ', 'ssp ', 'redemption', 'superfractor',
      // MLB teams (sampling major markets)
      'yankees', 'red sox', 'dodgers', 'cubs', 'mets', 'cardinals', 'braves',
      'giants', 'astros', 'phillies', 'padres', 'mariners', 'angels', 'orioles',
    ],
    weak: [
      'prospect', 'draft picks', 'mlb', 'major league',
    ],
    negative: [
      'pokemon', 'pokémon', 'yugioh', 'mtg', 'lorcana', 'one piece', 'digimon',
      'football', 'basketball', 'hockey', 'soccer', 'nfl', 'nba', 'nhl',
      'pikachu', 'charizard',
    ],
  },
  basketball: {
    definitive: [
      'panini basketball', 'nba trading cards', 'upper deck basketball',
      'panini prizm basketball', 'topps basketball',
    ],
    strong: [
      // Major products
      'prizm basketball', 'donruss optic basketball', 'select basketball', 
      'mosaic basketball', 'national treasures basketball', 'immaculate basketball',
      'fleer basketball', 'hoops basketball', 'contenders basketball',
      'court kings', 'noir basketball', 'chronicles basketball', 'revolution basketball',
      'optic basketball', 'spectra basketball', 'crown royale basketball',
    ],
    moderate: [
      // NBA teams/players likely to appear
      'lakers', 'celtics', 'warriors', 'bulls', 'heat', 'nets', 'knicks',
      'lebron', 'jordan', 'kobe', 'curry', 'durant', 'giannis', 'luka',
      'zion', 'ja morant', 'tatum', 'wemby', 'wembanyama', 'victor wembanyama',
      // Card types
      'rookie card', 'auto', 'patch', 'prizm', 'silver', 'gold', 'refractor',
    ],
    weak: [
      'nba', 'basketball',
    ],
    negative: [
      'pokemon', 'pokémon', 'yugioh', 'mtg', 'lorcana', 'one piece', 'digimon',
      'football', 'baseball', 'hockey', 'soccer', 'nfl', 'mlb', 'nhl',
      'pikachu', 'charizard',
    ],
  },
  football: {
    definitive: [
      'panini football', 'nfl trading cards', 'topps football',
      'panini prizm football', 'score football',
    ],
    strong: [
      // Major products
      'prizm football', 'donruss optic football', 'select football', 
      'mosaic football', 'national treasures football', 'immaculate football',
      'contenders football', 'playbook football', 'origins football',
      'chronicles football', 'spectra football', 'optic football',
      'absolute football', 'phoenix football', 'certified football',
      'plates and patches', 'flawless football',
    ],
    moderate: [
      // NFL teams/players
      'chiefs', 'eagles', '49ers', 'cowboys', 'bills', 'bengals', 'ravens',
      'mahomes', 'kelce', 'brady', 'allen', 'burrow', 'hurts', 'herbert',
      'trevor lawrence', 'cj stroud', 'lamar jackson', 'travis kelce',
      // Card types
      'rookie card', 'auto', 'patch', 'prizm', 'silver', 'gold',
    ],
    weak: [
      'nfl', 'football', 'quarterback', 'touchdown',
    ],
    negative: [
      'pokemon', 'pokémon', 'yugioh', 'mtg', 'lorcana', 'one piece', 'digimon',
      'basketball', 'baseball', 'hockey', 'soccer', 'nba', 'mlb', 'nhl',
      'pikachu', 'charizard',
    ],
  },
  hockey: {
    definitive: [
      'upper deck hockey', 'nhl trading cards', 'o-pee-chee hockey', 'opc hockey',
    ],
    strong: [
      // Major products
      'young guns', 'sp authentic hockey', 'the cup hockey', 'sp game used',
      'ice hockey', 'trilogy hockey', 'artifacts hockey', 'synergy hockey',
      'allure hockey', 'series 1 hockey', 'series 2 hockey', 'extended series hockey',
      'skybox hockey', 'mvp hockey', 'o-pee-chee platinum', 'opc platinum',
    ],
    moderate: [
      // NHL teams/players
      'oilers', 'maple leafs', 'canadiens', 'bruins', 'rangers', 'penguins',
      'gretzky', 'mcdavid', 'crosby', 'ovechkin', 'matthews', 'mackinnon',
      'bedard', 'connor bedard', 'draisaitl', 'connor mcdavid',
      // Card types
      'rookie card', 'auto', 'patch', 'young guns',
    ],
    weak: [
      'nhl', 'hockey',
    ],
    negative: [
      'pokemon', 'pokémon', 'yugioh', 'mtg', 'lorcana', 'one piece', 'digimon',
      'football', 'basketball', 'baseball', 'soccer', 'nfl', 'nba', 'mlb',
      'pikachu', 'charizard',
    ],
  },
  soccer: {
    definitive: [
      'panini soccer', 'topps soccer', 'uefa trading cards', 'topps chrome soccer',
      'panini prizm soccer', 'topps match attax',
    ],
    strong: [
      // Major products
      'topps chrome uefa', 'match attax', 'donruss soccer', 'select soccer',
      'merlin stickers', 'panini world cup', 'panini euro', 'topps merlin',
      'topps finest soccer', 'topps stadium club soccer', 'obsidian soccer',
      'chronicles soccer', 'mosaic soccer',
    ],
    moderate: [
      // Leagues/teams/players
      'premier league', 'champions league', 'la liga', 'bundesliga', 'serie a',
      'messi', 'ronaldo', 'mbappe', 'haaland', 'bellingham', 'vinicius',
      'manchester united', 'liverpool', 'chelsea', 'arsenal', 'man city',
      'real madrid', 'barcelona', 'bayern', 'psg', 'juventus',
      // Card types
      'rookie card', 'auto', 'patch', 'prizm', 'refractor',
    ],
    weak: [
      'soccer', 'football', 'futbol', 'fifa', 'uefa',
    ],
    negative: [
      'pokemon', 'pokémon', 'yugioh', 'mtg', 'lorcana', 'one piece', 'digimon',
      'nfl', 'basketball', 'baseball', 'hockey', 'nba', 'mlb', 'nhl',
      'american football', 'panini football', 'pikachu', 'charizard',
    ],
  },
};

/**
 * Main detection function using tiered approach:
 * 1. Check for definitive keywords (instant match, confidence = 1.0)
 * 2. Calculate weighted score for each game
 * 3. Apply heuristic fallbacks for edge cases
 * 4. Return 'other' with needsReview flag if no confident match
 */
export function detectGameWithConfidence(
  productName: string,
  setName: string = ''
): DetectionResult {
  const combined = `${productName} ${setName}`.toLowerCase();
  const matchedKeywords: string[] = [];
  
  // ========== TIER 1: Definitive Keywords (instant match) ==========
  for (const [game, config] of Object.entries(GAME_DETECTION_CONFIG)) {
    for (const kw of config.definitive) {
      if (combined.includes(kw)) {
        return {
          game,
          confidence: 1.0,
          needsReview: false,
          matchedKeywords: [kw],
        };
      }
    }
  }
  
  // ========== TIER 2: Weighted Scoring ==========
  const scores: Record<string, { score: number; matches: string[] }> = {};
  
  for (const [game, config] of Object.entries(GAME_DETECTION_CONFIG)) {
    let score = 0;
    const matches: string[] = [];
    
    // Check strong keywords
    for (const kw of config.strong) {
      if (combined.includes(kw)) {
        score += WEIGHT.STRONG;
        matches.push(kw);
      }
    }
    
    // Check moderate keywords
    for (const kw of config.moderate) {
      if (combined.includes(kw)) {
        score += WEIGHT.MODERATE;
        matches.push(kw);
      }
    }
    
    // Check weak keywords
    for (const kw of config.weak) {
      if (combined.includes(kw)) {
        score += WEIGHT.WEAK;
        matches.push(kw);
      }
    }
    
    // Apply negative penalties
    for (const kw of config.negative) {
      if (combined.includes(kw)) {
        score += WEIGHT.NEGATIVE;
      }
    }
    
    if (score > 0) {
      scores[game] = { score, matches };
    }
  }
  
  // Find highest scoring game
  const sortedScores = Object.entries(scores)
    .sort(([, a], [, b]) => b.score - a.score);
  
  if (sortedScores.length > 0) {
    const [topGame, { score, matches }] = sortedScores[0];
    const runnerUpScore = sortedScores[1]?.[1].score ?? 0;
    const dominant = score - runnerUpScore >= THRESHOLD.DOMINANT_MARGIN;
    
    // Only use if score meets minimum threshold
    if (score >= THRESHOLD.MIN_SCORE) {
      // Normalize confidence: score of 10+ = 1.0, scale down from there
      const confidence = Math.min(score / 10, 1.0);
      const needsReview = dominant ? false : confidence < THRESHOLD.HIGH_CONFIDENCE;
      
      return {
        game: topGame,
        confidence: dominant ? Math.max(confidence, 0.75) : confidence,
        needsReview,
        matchedKeywords: matches,
      };
    }
  }

  
  // ========== TIER 3: Heuristic Fallbacks ==========
  
  // Japanese/Chinese products with box/pack indicators are likely Pokemon
  // (Pokemon dominates the JP/CN sealed product market)
  const hasJpCnIndicator = combined.includes('(jp)') || 
                           combined.includes('(cn)') ||
                           combined.includes('japanese') ||
                           combined.includes('chinese');
  const hasProductIndicator = combined.includes('box') || 
                              combined.includes('pack') ||
                              combined.includes('booster') ||
                              combined.includes('etb') ||
                              combined.includes('collection');
  
  if (hasJpCnIndicator && hasProductIndicator) {
    return {
      game: 'pokemon',
      confidence: 0.6,
      needsReview: true,
      matchedKeywords: ['(jp/cn) + product indicator heuristic'],
    };
  }
  
  // Set code pattern matching
  const svPattern = /\bsv\d{1,2}\b/i;  // SV01, SV02, etc.
  const swshPattern = /\bswsh\d{1,3}\b/i;  // SWSH001, etc.
  const smPattern = /\bsm\d{1,2}\b/i;  // SM01, SM02, etc.
  
  if (svPattern.test(combined) || swshPattern.test(combined) || smPattern.test(combined)) {
    return {
      game: 'pokemon',
      confidence: 0.8,
      needsReview: false,
      matchedKeywords: ['Pokemon set code pattern'],
    };
  }
  
  // ========== No confident match ==========
  return {
    game: 'other',
    confidence: 0,
    needsReview: true,
    matchedKeywords: [],
  };
}

/**
 * Simple wrapper that returns just the game string for backward compatibility
 */
export function detectGame(productName: string, setName: string = ''): string {
  return detectGameWithConfidence(productName, setName).game;
}

/**
 * Collectr CSVs include a Category column that already names the game
 * (e.g. "Pokémon", "Magic", "One Piece", "Riftbound"). Trust that first,
 * fall back to portfolio name, then to keyword detection.
 */
const COLLECTR_CATEGORY_TO_GAME: Record<string, string> = {
  'pokemon': 'pokemon',
  'pokémon': 'pokemon',
  'pokemon tcg': 'pokemon',
  'magic': 'mtg',
  'magic the gathering': 'mtg',
  'magic: the gathering': 'mtg',
  'mtg': 'mtg',
  'yu-gi-oh': 'yugioh',
  'yu-gi-oh!': 'yugioh',
  'yugioh': 'yugioh',
  'ygo': 'yugioh',
  'one piece': 'onepiece',
  'onepiece': 'onepiece',
  'one piece tcg': 'onepiece',
  'lorcana': 'lorcana',
  'disney lorcana': 'lorcana',
  'riftbound': 'riftbound',
  'league of legends': 'riftbound',
  'digimon': 'digimon',
  'dragon ball': 'dragonball',
  'dragon ball super': 'dragonball',
  'dragonball': 'dragonball',
  'flesh and blood': 'fab',
  'flesh & blood': 'fab',
  'fab': 'fab',
  'star wars': 'starwars',
  'star wars unlimited': 'starwars',
  'swu': 'starwars',
  'weiss schwarz': 'weiss',
  'weiss': 'weiss',
  'vanguard': 'vanguard',
  'cardfight vanguard': 'vanguard',
  'final fantasy': 'finalfantasy',
  'final fantasy tcg': 'finalfantasy',
  'union arena': 'unionarena',
  'universus': 'universus',
  'gundam': 'gundam',
  'gundam card game': 'gundam',
  'grand archive': 'grandarchive',
  'battle spirits': 'battlespirits',
  'shadowverse': 'shadowverse',
};

function normalizeCategory(s: string): string {
  return (s || '').trim().toLowerCase();
}

function categoryToGame(raw: string): string | null {
  const norm = normalizeCategory(raw);
  if (!norm) return null;
  if (COLLECTR_CATEGORY_TO_GAME[norm]) return COLLECTR_CATEGORY_TO_GAME[norm];
  // Substring scan for portfolio names like "Pokemon Slabs 2024"
  for (const [key, game] of Object.entries(COLLECTR_CATEGORY_TO_GAME)) {
    if (norm.includes(key)) return game;
  }
  return null;
}

export function detectGameFromCollectr(
  collectrCategory: string,
  portfolioName: string,
  productName: string,
  setName: string = ''
): DetectionResult {
  // 1. Trust the Collectr Category column when it names a TCG we recognize.
  const catGame = categoryToGame(collectrCategory);
  if (catGame && catGame !== 'sports') {
    return {
      game: catGame,
      confidence: 1.0,
      needsReview: false,
      matchedKeywords: [`collectr:category=${collectrCategory}`],
    };
  }

  // 2. Portfolio names often restate the game.
  const portfolioGame = categoryToGame(portfolioName);
  if (portfolioGame && portfolioGame !== 'sports') {
    return {
      game: portfolioGame,
      confidence: 0.9,
      needsReview: false,
      matchedKeywords: [`collectr:portfolio=${portfolioName}`],
    };
  }

  // 3. Fall back to keyword detection.
  return detectGameWithConfidence(productName, setName);
}

